import React, { useState, useEffect, useCallback } from 'react';
import { Switch, Route, HashRouter as Router, useLocation } from 'react-router-dom';
// import Slides from './components/Slides'; // Componente original (desactivado temporalmente)
import SlidesMain from './components/SlidesMain';
import PhotoChecklistMain from './components/PhotoChecklistMain';
// import PhotoChecklist from './components/PhotoChecklist'; // Componente original (desactivado temporalmente)
import Camera from './components/Camera';
import PhotoReview from './components/PhotoReview';
import AnalisisGeneral from './components/AnalisisGeneral';
import ProcessImages from './components/ProcessImages';
import OrientationGuard from './components/OrientationGuard';
import { prewarmModel } from './utils/modelLoader';

const AppContent = ({
  photos,
  resultadosAnalisis,
  fotosTomadas,
  totalFotosRequeridas,
  enterFullscreen,
  handleSavePhoto,
  resetAnalisis,
  resetInspeccion,
  setResultadosAnalisis,
  syncStatus,
  lastSavedAt
}) => {
  const location = useLocation();

  return (
    <div
      className="App"
      onPointerDownCapture={enterFullscreen}
      onTouchStartCapture={enterFullscreen}
      onClick={enterFullscreen}
    >
      <OrientationGuard pathname={location.pathname} />
      {/* InspectionStepper removed — progress is shown inline per screen */}
      <div
        key={location.pathname}
        style={{
          animation: 'routeFadeSlide 220ms ease-out'
        }}
      >
        <style>{`@keyframes routeFadeSlide { from { opacity: 0; transform: translateY(6px);} to { opacity: 1; transform: translateY(0);} }`}</style>
        <Switch>
          <Route path="/" exact component={SlidesMain} />
          <Route path="/checklist" render={(props) => (
            <PhotoChecklistMain
              {...props}
              photos={photos}
              onSavePhoto={handleSavePhoto}
            />
          )} />
          <Route path="/camera/:photoType" render={(props) => (
            <Camera
              {...props}
              onSavePhoto={handleSavePhoto}
              fotosTomadas={fotosTomadas}
              totalFotosRequeridas={totalFotosRequeridas}
              resetAnalisis={resetAnalisis}
            />
          )} />
          <Route path="/review" component={PhotoReview} />
          <Route path="/process-images" render={(props) => (
            <ProcessImages
              {...props}
              setResultadosAnalisis={setResultadosAnalisis}
              totalFotosRequeridas={totalFotosRequeridas}
            />
          )} />
          <Route path="/analisis-general" render={(props) => (
            <AnalisisGeneral
              {...props}
              resultados={resultadosAnalisis}
              onResetAnalisis={resetAnalisis}
              onResetInspeccion={resetInspeccion}
            />
          )} />
        </Switch>
      </div>
    </div>
  );
};

function App() {
  const [photos, setPhotos] = useState(Array(11).fill(null));
  const [resultadosAnalisis, setResultadosAnalisis] = useState(null);
  const [fotosTomadas, setFotosTomadas] = useState(0);
  const totalFotosRequeridas = 11;
  const [syncStatus, setSyncStatus] = useState('saved');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const resetAnalisis = useCallback(() => {
    setResultadosAnalisis(null);
    localStorage.removeItem('imagenesProcessadas');
  }, []);

  const resetInspeccion = useCallback(() => {
    setPhotos(Array(11).fill(null));
    setResultadosAnalisis(null);
    setFotosTomadas(0);
    localStorage.clear();
  }, []);

  // Función para entrar en modo de pantalla completa
  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement && !document.msFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) { // Safari
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { // IE11
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) { // Firefox
        elem.mozRequestFullScreen();
      }
    }
  }, []);

  const estimateBase64SizeBytes = (base64Str) => {
    if (!base64Str || typeof base64Str !== 'string') return 0;
    const parts = base64Str.split(',');
    const payload = parts.length > 1 ? parts[1] : parts[0];
    return Math.ceil((payload.length * 3) / 4);
  };

  const getImageStorageProfile = (photoTypeIndex) => {
    if ([6, 7, 9, 10].includes(photoTypeIndex)) {
      return { maxDimension: 1900, quality: 0.95, targetBytes: 850 * 1024 };
    }

    if ([0, 1, 2, 3].includes(photoTypeIndex)) {
      return { maxDimension: 1500, quality: 0.92, targetBytes: 750 * 1024 };
    }

    return { maxDimension: 1400, quality: 0.9, targetBytes: 700 * 1024 };
  };

  // Optimiza solo cuando hace falta para evitar pérdida de calidad visible
  const optimizeImageForStorage = (base64Str, options) => {
    const { maxDimension, quality, targetBytes } = options;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const originalSize = estimateBase64SizeBytes(base64Str);

        if (
          img.width <= maxDimension &&
          img.height <= maxDimension &&
          originalSize <= targetBytes
        ) {
          resolve(base64Str);
          return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('No se pudo obtener contexto de canvas para optimizar imagen.'));
          return;
        }

        let { width, height } = img;

        const maxSide = Math.max(width, height);
        if (maxSide > maxDimension) {
          const ratio = maxDimension / maxSide;
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let currentQuality = quality;
        let output = canvas.toDataURL('image/jpeg', currentQuality);

        while (estimateBase64SizeBytes(output) > targetBytes && currentQuality > 0.82) {
          currentQuality -= 0.04;
          output = canvas.toDataURL('image/jpeg', currentQuality);
        }

        resolve(output);
      };
      img.onerror = (error) => {
        console.error('Error al cargar la imagen para optimización:', error);
        reject(error);
      };
    });
  };

  // Función para guardar la foto tomada
  const handleSavePhoto = async (photoTypeIndex, photoData) => {
    setSyncStatus('saving');
    try {
      let processedImageData;
      let ubicacionData = null;
      const profile = getImageStorageProfile(photoTypeIndex);

      if (typeof photoData === 'object' && photoData.imageData) {
        processedImageData = await optimizeImageForStorage(photoData.imageData, profile);
        ubicacionData = photoData.ubicacion;
      } else {
        processedImageData = await optimizeImageForStorage(photoData, profile);
      }

      let finalImageData = processedImageData;

      try {
        localStorage.setItem(`photo_${photoTypeIndex}`, finalImageData);
      } catch (storageError) {
        const emergencyProfile = {
          maxDimension: Math.max(1200, Math.round(profile.maxDimension * 0.85)),
          quality: Math.max(0.84, profile.quality - 0.08),
          targetBytes: Math.max(420 * 1024, Math.round(profile.targetBytes * 0.7))
        };
        finalImageData = await optimizeImageForStorage(finalImageData, emergencyProfile);
        localStorage.setItem(`photo_${photoTypeIndex}`, finalImageData);
        console.warn('Se aplicó optimización de emergencia por límite de almacenamiento local.');
      }

      if (ubicacionData) {
        localStorage.setItem(`photo_${photoTypeIndex}_ubicacion`, JSON.stringify(ubicacionData));
      }

      setPhotos(prevPhotos => {
        const newPhotos = [...prevPhotos];
        if (newPhotos[photoTypeIndex] === null) {
          setFotosTomadas(prev => prev + 1);
        }
        newPhotos[photoTypeIndex] = finalImageData;
        return newPhotos;
      });
      setSyncStatus('saved');
      setLastSavedAt(Date.now());
    } catch (error) {
      console.error("Error al procesar y guardar la foto:", error);
      setSyncStatus('error');
    }
  };

  // Recuperar las fotos almacenadas en localStorage al cargar la aplicación
  useEffect(() => {
    const storedPhotos = Array(11).fill(null).map((_, index) => {
      return localStorage.getItem(`photo_${index}`) || null;
    });
    setPhotos(storedPhotos);
  }, []);

  useEffect(() => {
    const warmupTimer = window.setTimeout(() => {
      prewarmModel('angulos');
    }, 1200);

    return () => window.clearTimeout(warmupTimer);
  }, []);

  return (
    <Router>
      <AppContent
        photos={photos}
        resultadosAnalisis={resultadosAnalisis}
        fotosTomadas={fotosTomadas}
        totalFotosRequeridas={totalFotosRequeridas}
        enterFullscreen={enterFullscreen}
        handleSavePhoto={handleSavePhoto}
        resetAnalisis={resetAnalisis}
        resetInspeccion={resetInspeccion}
        setResultadosAnalisis={setResultadosAnalisis}
        syncStatus={syncStatus}
        lastSavedAt={lastSavedAt}
      />
    </Router>
  );
}

export default App;
